import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import bookingService from '../services/bookingService';
import facilityService from '../services/facilityService';
import BookingsList from '../components/BookingsList';
import BookingEditModal from '../components/BookingEditModal';
import './BookingsPage.css';

const BookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // Edit modal state
  const [editingBooking, setEditingBooking] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadBookings();
  }, [selectedDate, selectedFacility, selectedStatus]);

  const loadInitialData = async () => {
    try {
      const [facilitiesData, statusesData] = await Promise.all([
        facilityService.getAllFacilities(),
        bookingService.getBookingStatuses()
      ]);
      setFacilities(facilitiesData);
      setStatuses(statusesData);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Fehler beim Laden der Daten');
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {
        date: selectedDate,
        active: true
      };
      
      if (selectedFacility) {
        filters.facilityId = selectedFacility;
      }
      
      if (selectedStatus) {
        filters.statusId = selectedStatus;
      }

      const data = await bookingService.getBookingsDetailed(filters);
      setBookings(data);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError('Fehler beim Laden der Buchungen');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatusId) => {
    try {
      await bookingService.updateBookingStatus(bookingId, newStatusId);
      // Reload bookings to show updated status
      loadBookings();
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError('Fehler beim Aktualisieren des Status');
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleFacilityChange = (e) => {
    setSelectedFacility(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
  };

  const handleSaveBooking = async (bookingId, data) => {
    try {
      await bookingService.updateBooking(bookingId, data);
      setEditingBooking(null);
      loadBookings();
    } catch (err) {
      console.error('Error updating booking:', err);
      throw err;
    }
  };

  const handleCloseModal = () => {
    setEditingBooking(null);
  };

  return (
    <div className="bookings-page">
      <div className="page-header">
        <h1>Buchungsübersicht</h1>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="date-filter">Datum:</label>
          <input
            id="date-filter"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="facility-filter">Anlage:</label>
          <select
            id="facility-filter"
            value={selectedFacility}
            onChange={handleFacilityChange}
            className="filter-select"
          >
            <option value="">alle Anlagen</option>
            {facilities.map(facility => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status:</label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={handleStatusChange}
            className="filter-select"
          >
            <option value="">alle Status</option>
            {statuses.map(status => (
              <option key={status.id} value={status.id}>
                {status.description || status.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      {loading ? (
        <div className="loading">Buchungen werden geladen...</div>
      ) : (
        <BookingsList
          bookings={bookings}
          statuses={statuses}
          onStatusUpdate={handleStatusUpdate}
          onEditBooking={handleEditBooking}
        />
      )}

      {editingBooking && (
        <BookingEditModal
          booking={editingBooking}
          onSave={handleSaveBooking}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default BookingsPage;